'use client';

import { Form } from 'informed';
import { usePathname, useSearchParams } from 'next/navigation';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWRMutation from 'swr/mutation';

import { type NewPassword, recoveryPassword } from '@/api/auth';
import { Button } from '@/components/buttons';
import { ScLoginWrapButtons } from '@/components/dialogs/Login/styled';
import { FormGroup } from '@/components/form/groups/FormGroup';
import { Password } from '@/components/form/Password';
import { showTooltip } from '@/helpers/tooltip';
import { translationFactory } from '@/helpers/transl';
import { validatePassword } from '@/helpers/validators';
import { useNavigation } from '@/hooks/navigation';
import { adaptMutation } from '@/modules/common/helpers/swr';
import { TooltipTypes } from '@/store/tooltipStore';
import { ScContainer, ScFormBlock } from '@/styles/common';

export const EditPassword: FC = () => {
  const { navigate } = useNavigation();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const resetPasswordToken = searchParams.get('reset_password_token');

  useEffect(() => {
    if (pathname.includes('/users/password/edit') && !resetPasswordToken) {
      navigate('/', true);
    }
  }, [resetPasswordToken, pathname, navigate]);

  const [error, setError] = useState<string>();

  const { t } = useTranslation('common');

  const { trigger: requestRecoveryPassword, isMutating } = useSWRMutation(
    'recoveryPassword',
    adaptMutation(recoveryPassword),
    {
      onSuccess: () => {
        showTooltip({
          type: TooltipTypes.SUCCESS,
          translate: true,
          body: translationFactory('common:auth.recovery.success'),
        });
        navigate('/', true);
      },
      onError: () => {
        showTooltip({
          type: TooltipTypes.ERROR,
          translate: true,
          body: translationFactory('common:auth.recovery.error'),
        });
        navigate('/', true);
      },
    }
  );

  const handleSubmit = useCallback(
    (values: NewPassword) => {
      if (values.password !== values.passwordConfirmation) {
        setError('common:auth.recovery.passwordsNotMatch');
        return;
      }

      if (!resetPasswordToken) return;

      requestRecoveryPassword({
        ...values,
        resetPasswordToken,
      });
    },
    [resetPasswordToken, requestRecoveryPassword]
  );

  return (
    <ScContainer>
      <ScFormBlock>
        <h1>{t('auth.recovery.changeYourPassword')}</h1>
        <Form<NewPassword> onSubmit={({ values }) => handleSubmit(values)}>
          <FormGroup title={t('auth.recovery.newPassword')}>
            <Password
              name="password"
              strengthChecker
              validate={validatePassword}
              validateOn="change"
            />
          </FormGroup>
          <FormGroup title={t('auth.recovery.confirmPassword')}>
            <Password
              error={error && t(error)}
              name="passwordConfirmation"
              validateOn="blur"
            />
          </FormGroup>

          <ScLoginWrapButtons>
            <Button isLoading={isMutating} large primary type="submit" wide>
              {t('auth.recovery.changeMyPassword')}
            </Button>
          </ScLoginWrapButtons>
        </Form>
      </ScFormBlock>
    </ScContainer>
  );
};
